import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import client from '../api/client';
import { readCacheMeta, getCacheMetaEventName } from '../api/cacheMeta';
import {
  createOfflineUuid,
  enqueuePendingWrite,
  flushPendingWrites,
  getPendingWritesCount,
  getPendingWritesEventName,
  readPendingWrites,
  resolveResponseUuid,
} from '../api/offlineMutationQueue';
import { registerUuidAlias } from '../offline/db';
import { publishCollectionChange } from '../utils/collectionSync';

const CacheContext = createContext(null);
const POPULATION_RESET_ACTIONS = ['draft', 'revise', 'recall', 'reset', 'revert'];

function readLastCachedDate() {
  const meta = readCacheMeta();
  return meta.lastCached ? new Date(meta.lastCached) : null;
}

export function CacheProvider({ children }) {
  const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastCached, setLastCached] = useState(() => readLastCachedDate());
  const [pendingCount, setPendingCount] = useState(() => getPendingWritesCount());
  const syncingRef = useRef(false);
  const wasOffline = useRef(typeof navigator === 'undefined' ? false : !navigator.onLine);

  const refreshState = useCallback(() => {
    setPendingCount(getPendingWritesCount());
    setLastCached(readLastCachedDate());
  }, []);

  const flushPending = useCallback(async () => {
    if (syncingRef.current) return;

    const pending = readPendingWrites();
    if (pending.length === 0) {
      refreshState();
      return;
    }

    syncingRef.current = true;
    setIsSyncing(true);

    try {
      const executeQueuedRequest = async (item) => {
        const method = item.method ?? 'post';
        const baseUrl = String(item.url ?? '').trim();

        if (item.collectionKey === 'population-records' && item.action === 'reset' && baseUrl) {
          const parentUrl = baseUrl.replace(/\/[^/]+$/, '');
          let lastError = null;

          for (const action of POPULATION_RESET_ACTIONS) {
            try {
              return await client.request({
                method,
                url: `${parentUrl}/${action}`,
                data: item.data ?? undefined,
                params: item.params ?? undefined,
                headers: item.headers ?? undefined,
              });
            } catch (error) {
              lastError = error;
              if (error?.response?.status !== 404) throw error;
            }
          }

          throw lastError ?? new Error('Unable to sync reset action.');
        }

        return client.request({
          method,
          url: item.url,
          data: item.data ?? undefined,
          params: item.params ?? undefined,
          headers: item.headers ?? undefined,
        });
      };

      await flushPendingWrites(
        executeQueuedRequest,
        {
          onSuccess: (item, response) => {
            const resolvedUuid = resolveResponseUuid(response);
            if (item.tempUuid && resolvedUuid && resolvedUuid !== item.tempUuid) {
              registerUuidAlias(item.tempUuid, resolvedUuid);
            }

            if (item.collectionKey) {
              publishCollectionChange(item.collectionKey, {
                action: item.action ?? 'sync',
                uuid: resolvedUuid ?? item.recordUuid ?? item.tempUuid ?? null,
                synced: true,
              });
            }
          },
        }
      );
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
      refreshState();
    }
  }, [refreshState]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOnline = async () => {
      setIsOnline(true);
      if (wasOffline.current) {
        await flushPending();
      }
      wasOffline.current = false;
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOffline.current = true;
    };

    const handleStateChange = () => refreshState();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener(getPendingWritesEventName(), handleStateChange);
    window.addEventListener(getCacheMetaEventName(), handleStateChange);
    window.addEventListener('storage', handleStateChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener(getPendingWritesEventName(), handleStateChange);
      window.removeEventListener(getCacheMetaEventName(), handleStateChange);
      window.removeEventListener('storage', handleStateChange);
    };
  }, [flushPending, refreshState]);

  const queueWrite = useCallback((key, data) => {
    const queued = enqueuePendingWrite({
      collectionKey: key,
      action: 'write',
      method: 'post',
      url: String(key ?? ''),
      data,
      tempUuid: createOfflineUuid(key),
    });
    setPendingCount(getPendingWritesCount());
    return queued;
  }, []);

  const getCachedData = useCallback(() => null, []);

  return (
    <CacheContext.Provider
      value={{
        isOnline,
        isSyncing,
        lastCached,
        pendingCount,
        getCachedData,
        queueWrite,
      }}
    >
      {children}
    </CacheContext.Provider>
  );
}

export const useCache = () => useContext(CacheContext);
